/**
 * This file contains API functions for user management and system health checks.
 */
import getAPIEndpoint from "./APIEndpoint";

/**
 * Fetches the complete list of users from the API.
 * @returns {Promise<any>} A promise that resolves to the list of users.
 */
export const GetFullListOfUsers = async () => {
  return fetch(`${getAPIEndpoint()}/users`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      authorization: `${localStorage.getItem("token")}`,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error getting users:", error));
};

/**
 * Updates a user's role in the system.
 * @param {string} userId - The ID of the user to update.
 * @param {string} role - The new role to assign to the user.
 * @returns {Promise<any>} A promise that resolves to the updated user data.
 */
export const updateUserRole = async (userId: string, role: string) => {
  return fetch(`${getAPIEndpoint()}/users/${userId}/role`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      authorization: `${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ role }),
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error updating user role:", error));
};

/**
 * Retrieves the current database version from the health endpoint.
 * Uses different endpoints based on whether the application is running locally or in production.
 * @returns {Promise<string>} A promise that resolves to the database version string.
 */
export const getDatabaseVersion = async (): Promise<string> => {
  const useLocalApi = localStorage.getItem("useLocalApi");
  const isLocalEnvironment = window.location.href.includes("localhost");

  let endpoint = "https://jupiter.wealthbee.io/Health/pong";

  // If we're on localhost and useLocalApi is unset or true
  if (isLocalEnvironment && (useLocalApi === null || useLocalApi === "true")) {
    endpoint = "http://localhost:5100/api/Health/pong";
  }

  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      authorization: `${localStorage.getItem("token")}`,
    },
  });
  const data = await response.text();
  return data.toString();
};

/**
 * Admin purge API call. Requires admin auth token.
 */
export const purgeAllClientData = async (): Promise<{ status: string } | { error: string }> => {
  const response = await fetch(`${getAPIEndpoint()}/admin/purge`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      authorization: `${localStorage.getItem("token")}`,
    },
  });
  try {
    return await response.json();
  } catch (e) {
    return { error: "Failed to parse response" } as any;
  }
};
